import React, { useState, useEffect } from "react";

const formatValue = value =>
  value === null || value === undefined || value === "" ? "(empty)" : value;

const NotesModal = props => {
  const { account, onClose } = props;
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("notes");

  const fetchHistory = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(
        `/sitecore/shell/B2C/AdminManagement/GetUserHistoryAsync?userId=${account.userId}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
      );
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setHistory(Array.isArray(data) ? data : []);
    } catch (e) {
      setError("Unable to load history. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const addNote = async () => {
    if (!newNote.trim()) {
      setError("Please enter a note.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const response = await fetch(
        "/sitecore/shell/B2C/AdminManagement/AddUserNoteAsync",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            UserId: account.userId,
            Text: newNote.trim(),
          }),
        },
      );
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setHistory(Array.isArray(data) ? data : []);
      setNewNote("");
    } catch (e) {
      setError("Unable to save the note. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const sortedHistory = [...history].sort(
    (a, b) => new Date(b.createdDateUtc) - new Date(a.createdDateUtc),
  );
  const notes = sortedHistory.filter(e => e.entryType === "Note");
  const auditTrail = sortedHistory.filter(e => e.entryType === "FieldChange");

  const renderMeta = entry => (
    <div className="sso-account-approval__note-meta">
      {entry.createdBy || "Unknown"} &middot;{" "}
      {entry.createdDateUtc
        ? new Date(entry.createdDateUtc).toLocaleString()
        : ""}
    </div>
  );

  return (
    <div className="sso-account-approval__modal" onClick={onClose}>
      <div
        className="sso-account-approval__modal-content"
        onClick={e => e.stopPropagation()}
      >
        <button
          type="button"
          className="sso-account-approval__close-modal"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        <div className="sso-account-approval__modal-title txt-h3">
          History for {account.firstName} {account.lastName}
        </div>

        <div className="sso-account-approval__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "notes"}
            className={`sso-account-approval__tab ${
              activeTab === "notes" ? "sso-account-approval__tab--active" : ""
            }`}
            onClick={() => setActiveTab("notes")}
          >
            Notes
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "audit"}
            className={`sso-account-approval__tab ${
              activeTab === "audit" ? "sso-account-approval__tab--active" : ""
            }`}
            onClick={() => setActiveTab("audit")}
          >
            Audit Trail
          </button>
        </div>

        {activeTab === "notes" && (
          <>
            <div className="sso-account-approval__notes-list">
              {loading && <p>Loading...</p>}
              {!loading && notes.length === 0 && (
                <p>No notes have been added yet.</p>
              )}
              {!loading && notes.length > 0 && (
                <ul>
                  {notes.map((note, idx) => (
                    <li key={idx} className="sso-account-approval__note">
                      {note.action && note.action !== "Note" && (
                        <span className="sso-account-approval__note-action">
                          {note.action}
                        </span>
                      )}
                      <div className="sso-account-approval__note-text">
                        {note.noteText}
                      </div>
                      {renderMeta(note)}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="sso-account-approval__note-add">
              <label
                htmlFor="new-note"
                className="sso-account-approval__filter-popout-label"
              >
                Add a note
              </label>
              <textarea
                id="new-note"
                rows={3}
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
                placeholder="Enter a note..."
                className="sso-account-approval__note-add-input"
              />
              {error && (
                <div className="sso-account-approval__note-error">{error}</div>
              )}
              <div className="sso-account-approval__filter-popout-actions">
                <button className="btn" onClick={onClose} disabled={saving}>
                  Close
                </button>
                <button
                  className="btn btn--primary"
                  onClick={addNote}
                  disabled={saving || !newNote.trim()}
                >
                  {saving ? "Saving..." : "Add Note"}
                </button>
              </div>
            </div>
          </>
        )}

        {activeTab === "audit" && (
          <div className="sso-account-approval__notes-list">
            {loading && <p>Loading...</p>}
            {!loading && auditTrail.length === 0 && (
              <p>No field changes have been recorded yet.</p>
            )}
            {!loading && auditTrail.length > 0 && (
              <ul>
                {auditTrail.map((entry, idx) => (
                  <li key={idx} className="sso-account-approval__note">
                    {entry.action && (
                      <span className="sso-account-approval__note-action">
                        {entry.action}
                      </span>
                    )}
                    <div className="sso-account-approval__note-text">
                      <strong>{entry.fieldName}</strong>:{" "}
                      {formatValue(entry.oldValue)} &rarr;{" "}
                      {formatValue(entry.newValue)}
                    </div>
                    {renderMeta(entry)}
                  </li>
                ))}
              </ul>
            )}
            {error && (
              <div className="sso-account-approval__note-error">{error}</div>
            )}
            <div className="sso-account-approval__filter-popout-actions">
              <button className="btn" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotesModal;
